import React, { useState } from 'react';

import { cn, Nav, NavItem, NavSection, usePersistentState } from '@crossnokaye/ux-common';

import { AtlasRouteIds } from '@/components/navigation/constants';
import { SequencerNav } from '@/components/navigation/left/components/shared/sequencer-nav';
import { useNavConfig } from '@/components/navigation/left/components/shared/use-nav-config';
import { NavLink } from '@/components/navigation/nav-link';
import { NavItemConfig } from '@/components/navigation/types';
import { LocalStorageKeys } from '@/constants';
import { useNavStore } from '@/stores/nav';

interface ExpandedMainNavProps {
  onSelect: (id: string) => void;
}

export const ExpandedMainNav: React.FC<ExpandedMainNavProps> = ({ onSelect }) => {
  const { accessibleNavItems, selectedNavItemId } = useNavStore();
  const { getSequencerNavConfig, getSystemViewNavConfig } = useNavConfig();

  const categories = Object.entries(accessibleNavItems);

  const [value, setValue] = usePersistentState<string[]>(
    localStorage,
    LocalStorageKeys.ExpandedNavSections
  );

  const [defaultExpandedSections, setDefaultExpandedSections] = useState<string[]>(value ?? []);

  const handleExpandSection = async (sections: string[]) => {
    setDefaultExpandedSections(sections);
    await setValue(sections);
  };

  return (
    <Nav
      className={cn('justify-start w-full z-0', 'flex-grow overflow-y-auto scrollbar-hide')}
      defaultOpen={defaultExpandedSections}
      onOpenChange={handleExpandSection}
    >
      {categories.map(([category, items]) => (
        <NavSection key={category} header={category} className={'font-bold text-xs mb-4 px-3.5'}>
          {items.map((item) => {
            const {
              sequencers,
              searchParams: sequencerSearchParams,
              useSequencerNav,
            } = getSequencerNavConfig(item);
            const { searchParams: systemViewSearchParams } = getSystemViewNavConfig(item);

            const navItem = (
              <NavItem
                key={item.id}
                id={item.id}
                label={item.label}
                className={cn(
                  'hover:bg-popover',
                  item.id === AtlasRouteIds.Support && 'text-muted-foreground'
                )}
                icon={item.icon}
                selected={item.id === selectedNavItemId}
                onClick={() => (useSequencerNav ? undefined : onSelect(item.id))}
                data-testid={`expanded-nav-item-${item.id}`}
                link={hasLinkChevronByCategory(item) || useSequencerNav}
              />
            );

            if (useSequencerNav && sequencers) {
              return (
                <SequencerNav
                  key={item.id}
                  onSelect={() => onSelect(item.id)}
                  routeId={item.id}
                  sequencerDevices={sequencers}
                >
                  <div className={'w-full'}>{navItem}</div>
                </SequencerNav>
              );
            }

            return (
              <NavLink
                key={item.id}
                routeId={item.id}
                searchParams={systemViewSearchParams || sequencerSearchParams || undefined}
              >
                {navItem}
              </NavLink>
            );
          })}
        </NavSection>
      ))}
    </Nav>
  );
};

// admin and support are the only categories that should have link chevrons
// except for Support and Facility Management
const hasLinkChevronByCategory = (item: NavItemConfig) => {
  const chevronItems = [
    AtlasRouteIds.HelpCenter,
    AtlasRouteIds.SiteNavigation,
    AtlasRouteIds.FacilityConfig,
  ] as const;
  return chevronItems.includes(item.id as (typeof chevronItems)[number]);
};
